import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { useApp } from "@/context/AppContext";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, money } = useApp();
  const navigate = useNavigate();
  const subtotal = cart.reduce((s, i) => s + i.price_usd * i.qty, 0);
  const FREE = 500;
  const remaining = Math.max(0, FREE - subtotal);

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-[80]" onClick={() => setCartOpen(false)} />
          <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
            data-testid="cart-drawer"
            className="fixed top-0 right-0 bottom-0 w-full max-w-md z-[90] flex flex-col" style={{ background: "var(--ev-surface)" }}>
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: "var(--ev-border)" }}>
              <span className="font-serif text-xl">Shopping Bag</span>
              <button data-testid="cart-close" onClick={() => setCartOpen(false)}><X size={22} strokeWidth={1.2} /></button>
            </div>

            {subtotal > 0 && (
              <div className="px-6 py-3 text-center text-xs tracking-wide border-b" style={{ borderColor: "var(--ev-border)", color: "var(--ev-muted)" }}>
                Complimentary shipping on all orders
              </div>
            )}

            <div className="flex-1 overflow-y-auto no-scrollbar">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-10" style={{ color: "var(--ev-muted)" }}>
                  <ShoppingBag size={40} strokeWidth={0.8} />
                  <p className="font-serif text-xl mt-5 mb-2" style={{ color: "var(--ev-text)" }}>Your bag is empty</p>
                  <p className="text-sm">Discover the Maison's latest creations.</p>
                </div>
              ) : cart.map((it) => (
                <div key={it.key} data-testid={`cart-item-${it.slug}`} className="flex gap-4 p-6 border-b" style={{ borderColor: "var(--ev-border)" }}>
                  <img src={it.image} alt={it.name} className="w-20 h-24 object-cover cursor-pointer" onClick={() => { setCartOpen(false); navigate(`/product/${it.slug}`); }} />
                  <div className="flex-1">
                    <div className="flex justify-between gap-2">
                      <span className="font-serif text-base leading-tight">{it.name}</span>
                      <button data-testid={`cart-remove-${it.slug}`} onClick={() => removeFromCart(it.key)}><X size={15} /></button>
                    </div>
                    <div className="text-[0.68rem] uppercase tracking-wider mt-1" style={{ color: "var(--ev-muted)" }}>
                      {it.storefront}{it.size ? ` · ${it.size}` : ""}{it.color ? ` · ${it.color}` : ""}
                    </div>
                    {(it.options || []).map((o, oi) => (
                      <div key={oi} className="text-[0.66rem] mt-0.5" style={{ color: "var(--ev-muted)" }}>{o.group}: {o.choice}{o.price_usd ? ` (+${money(o.price_usd)})` : ""}</div>
                    ))}
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border" style={{ borderColor: "var(--ev-border)" }}>
                        <button className="px-2 py-1" onClick={() => updateQty(it.key, it.qty - 1)}><Minus size={12} /></button>
                        <span className="px-3 text-sm">{it.qty}</span>
                        <button className="px-2 py-1" onClick={() => updateQty(it.key, it.qty + 1)}><Plus size={12} /></button>
                      </div>
                      <span className="text-sm">{money(it.price_usd * it.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {cart.length > 0 && (
              <div className="p-6 border-t space-y-4" style={{ borderColor: "var(--ev-border)" }}>
                <div className="flex justify-between font-serif text-lg">
                  <span>Subtotal</span><span>{money(subtotal)}</span>
                </div>
                <p className="text-xs" style={{ color: "var(--ev-muted)" }}>Shipping, taxes & duties calculated at checkout.</p>
                <button data-testid="cart-checkout-btn" onClick={() => { setCartOpen(false); navigate("/checkout"); }} className="btn-luxe w-full">Proceed to Checkout</button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
